import { useCallback, useEffect, useState } from 'react'
import { toast } from '../../lib/toast'
import {
  fetchBusinessSources,
  onSourceConnected,
  SOURCES_CHANGED_EVENT,
  type BusinessSource,
} from '../../lib/sources'

export type SourcesStatus = 'loading' | 'success' | 'error'

/**
 * Loads `business_sources()` and keeps it fresh: refetches on
 * SOURCES_CHANGED_EVENT and when the OAuth popup reports back. While a
 * mailbox is syncing it polls every 10 seconds so the badge settles
 * without a reload.
 */
export function useBusinessSources() {
  const [sources, setSources] = useState<BusinessSource[]>([])
  const [status, setStatus] = useState<SourcesStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const refetch = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const rows = await fetchBusinessSources()
        if (cancelled) return
        setSources(rows)
        setStatus('success')
        setError(null)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not load your sources.')
        setStatus('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tick])

  useEffect(() => {
    const onChanged = () => refetch()
    window.addEventListener(SOURCES_CHANGED_EVENT, onChanged)
    const off = onSourceConnected(({ ok, provider, message }) => {
      const noun = provider === 'gmail' ? 'Gmail' : 'Zoho Mail'
      if (ok) toast(`${noun} connected. Pulling in emails with your contacts now.`, 'success')
      else toast(`${noun} connect failed: ${message || 'unknown error'}`, 'error')
      refetch()
    })
    return () => {
      window.removeEventListener(SOURCES_CHANGED_EVENT, onChanged)
      off()
    }
  }, [refetch])

  const syncing = sources.some((s) => s.status === 'syncing')
  useEffect(() => {
    if (!syncing) return
    const t = setInterval(refetch, 10_000)
    return () => clearInterval(t)
  }, [syncing, refetch])

  return { sources, status, error, refetch }
}
